import {
  singleValueTimeSegment,
  timeSliderGranularityToTimeSelectionInput,
} from '../../../pages/dashboard/balance-sheet/backend-metrics/utils';
import {
  PbsService,
  PbsServiceResponseData,
  ValueByDate,
} from '../../../pages/dashboard/balance-sheet/types';
import {
  executeSqlQuery,
  formatPeriodConfigDate,
  getGranularityConfig,
  getPbsServiceResponseData,
  getTimeSlicesAccToTimeSliderConfig,
} from '../../../pages/dashboard/balance-sheet/utils';
import {
  ApiCustomSqlQueryResponse,
  ApiMasterDataQueryResponseDataPoint,
} from '../../api/api-interfaces';
import { CachedMasterDataService } from '../../api/cached-master-data-service';
import {
  CohortMetricId,
  GroupingInput,
  MetricGroupId,
  MetricId,
  TimeSelectionInput,
} from '../../graphql/generated/graphql-sdk';
import { getModifiedInputsAndFiltersForBenchmark } from '../../services/BenchmarkWrapperService';
import { getReactQueryWrappedService } from '../../services/utils';
import { uuid } from '../../utilFunctions/utils';
import { SQLFilters } from '../../v2/api/types';
import { Filter } from '../../v2/common/components/filter/filterbar/types';
import { TO_BE_DEPRECATED_getFilterConditionsForMetricGroup } from './utils';

export const getPbsOverTimeServiceForRegularBackendMetric = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricGroupId: MetricGroupIdInstance,
  cachedMasterDataApiService: CachedMasterDataService
) => {
  return getPbsOverTimeServiceForBackendMetric(
    metricGroupId,
    //TODO: fix it later
    // @ts-ignore
    cachedMasterDataApiService.queryMetrics
  );
};

export const getPbsOverTimeServiceForCohortBackendMetric = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricGroupId: MetricGroupIdInstance,
  cachedMasterDataApiService: CachedMasterDataService
) => {
  return getPbsOverTimeServiceForBackendMetric(
    metricGroupId,
    (
      timeSelection: TimeSelectionInput,
      metrics: MetricId[],
      userFilters?: SQLFilters | null,
      grouping?: GroupingInput,
      disableNestLoop?: boolean
    ) =>
      cachedMasterDataApiService.queryCohortMetrics(
        timeSelection,
        metrics as unknown as CohortMetricId[],
        userFilters,
        grouping,
        disableNestLoop
      )
  );
};

export const getPbsOverTimeServiceForEmployeeCohortBackendMetric = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricGroupId: MetricGroupIdInstance,
  cachedMasterDataApiService: CachedMasterDataService
) => {
  return getPbsOverTimeServiceForBackendMetric(
    metricGroupId,
    //TODO: fix it later
    // @ts-ignore
    cachedMasterDataApiService.queryEmployeeCohortMetrics
  );
};

const getPbsOverTimeServiceForBackendMetric = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricGroupId: MetricGroupIdInstance,
  queryMetrics: (
    timeSelection: TimeSelectionInput,
    metrics: MetricId[],
    userFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ) => Promise<ApiCustomSqlQueryResponse>
) => {
  const MetricService: PbsService<MetricGroupIdInstance> = async (
    inputs,
    filters,
    latestVersions,
    legacyStores,
    legacyDomainScopedStores
  ) => {
    const { metricStore } = legacyDomainScopedStores;
    const metrics =
      metricStore.getAllowedSortedMetricIdsForMetricGroup(metricGroupId);
    const metricIds = metrics.map((m) => m.id);
    const { timeSliderConfig, selectedBenchmark } = inputs;

    const sqlUserFilters = TO_BE_DEPRECATED_getFilterConditionsForMetricGroup(
      filters as Filter[],
      metricGroupId,
      metricStore
    );

    const backendOverTimePromise = () =>
      queryMetrics(
        timeSliderGranularityToTimeSelectionInput(timeSliderConfig),
        metricIds,
        sqlUserFilters
      );
    const backendTotalPeriodPromise = () =>
      queryMetrics(
        singleValueTimeSegment(timeSliderConfig),
        metricIds,
        sqlUserFilters
      );

    let benchmarkPromise:
      | (() => Promise<{ dataPoints: ApiMasterDataQueryResponseDataPoint[] }>)
      | null = null;
    if (selectedBenchmark) {
      const { inputs: benchmarkInputs, filters: benchmarkFilters } =
        getModifiedInputsAndFiltersForBenchmark(
          inputs,
          filters,
          [],
          timeSliderConfig,
          selectedBenchmark
        );
      const benchmarkTimeSliderConfig = benchmarkInputs.timeSliderConfig;
      const benchmarkSqlUserFilters =
        TO_BE_DEPRECATED_getFilterConditionsForMetricGroup(
          benchmarkFilters as Filter[],
          metricGroupId,
          metricStore
        );

      benchmarkPromise = () =>
        metricIds.length > 0
          ? queryMetrics(
              singleValueTimeSegment(benchmarkTimeSliderConfig),
              metricIds,
              benchmarkSqlUserFilters
            )
          : Promise.resolve({ dataPoints: [] });
    }

    const executeBackendSqlQuery = executeSqlQuery(
      metrics,
      selectedBenchmark,
      backendOverTimePromise,
      backendTotalPeriodPromise,
      benchmarkPromise
    );
    const responseData = await executeBackendSqlQuery;

    const timeSlices = getTimeSlicesAccToTimeSliderConfig(timeSliderConfig);

    return {
      [metricGroupId]: getPbsServiceResponseData(
        responseData.values[metricGroupId] as ValueByDate,
        timeSlices,
        formatPeriodConfigDate(getGranularityConfig(timeSliderConfig)),
        selectedBenchmark
      ),
    } as Record<MetricGroupIdInstance, PbsServiceResponseData>;
  };

  MetricService.cacheKey = uuid();
  return getReactQueryWrappedService(MetricService);
};
